/** @format */

import React, {useCallback, useContext} from 'react';

import {QuoteContext} from 'frontier-quoter';
import useDieSelection from './hooks/use_die_selection';

import {GridSelect, IconButton, Input, Popover, Radio, RadioGroup} from '@sttark/design-system';

import Section from './section';
import SizeSelect from './size_select';
import ArtworkUploader from './artwork_uploader';
import OrderSummary from './order_summary';
import CartonLaminateSelect from './carton_laminate_select';
import {
    PRODUCT_TYPE_CARTON,
    CARTON_SUBSTRATES,
    NO_LAMINATE_SUBSTRATES,
    LAMINATE_NO_LAMINATE,
    CARTON_BRIGHT_WHITE_PAPER,
    CARTON_UNLAMINATED_WHITE_PAPER,
    CARTON_WHITE_PAPER,
    CARTON_WHITE_PAPER_C2S,
    SHAPE_CARTON_AUTOLOCK,
    SIZE_CONSTRAINTS,
    SHAPE_CARTON_CUSTOM,
    SIZE_TYPE_STANDARD,
    CARTON_METALLIC_PAPER,
    CARTON_BLACK_VELLUM,
    CARTON_HOLOGRAPHIC,
    DUPLEX_SUBSTRATES,
    QUOTER_ROUNDED_CORNER,
    QUOTER_SQUARE_CORNER,
    SUBSTRATE_BACKING_PAPER,
    SUBSTRATE_BACKING_POLY,
    EMBOSSING_TYPE_ALL,
    EMBOSSING_TYPE_UNIQUE
} from '../../utilities/constants';
import cartonBoxAutolock from './assets/carton-box-autolock.svg';
import cartonBoxStraighTuck from './assets/carton-box-straight-tuck.svg';
import SelectedDie from './selected_die';
import ShapeSelect from './shape_select';
import SVGDownload from 'frontier/ds/quoter/svg_download';
import AdditionalOptions from './additional_options';
import {ConditionalButton, AccountContext} from 'frontier-utilities';
import {isValueMultipleOfStep} from 'frontier/utilities/dimension_validator';
import {OptionDescription} from 'frontier/ds/quoter/laminate_material_select_descriptions';
import CustomShapeSelect from 'frontier/ds/quoter/custom_shape_select';
import backingPaper from 'frontier/ds/quoter/assets/backing-paper.svg';
import backingPoly from 'frontier/ds/quoter/assets/backing-poly.svg';
import {CardSelect} from '@sttark/design-system';
import {useCartonSimilarSizes} from '@/ds/quoter/hooks/use_carton_similar_sizes';
import {SimilarSizesList} from '@/ds/quoter/similar_sizes_list';

export default function CartonQuoter() {
    const ctx = useContext(QuoteContext);
    const accountCtx = useContext(AccountContext);
    const order = ctx.order;
    const {errors} = ctx.handleIsComplete(order);

    const {
        width,
        setWidth,
        setShapeCategory,
        length,
        depth,
        setAllSizes,
        setDepth,
        setLength,
        shapeCategory,
        selectedDie,
        cartonDies,
        cartonCustomDies,
        isNewDie
    } = useDieSelection({
        order: ctx.order,
        updateOrder: patch => ctx.handleChange({order: {$merge: patch}})
    });

    const {sizes: cartonSimilarSizes} = useCartonSimilarSizes({
        cartonDies,
        width,
        length,
        depth,
        shapeId: order.shape_id
    });

    const isLaminateRequired = NO_LAMINATE_SUBSTRATES.indexOf(ctx.order.substrate_id) === -1;

    const enabledSections = {};
    enabledSections.laminate = !!order.substrate_id && isLaminateRequired;
    enabledSections.shape = isLaminateRequired ? !!order.laminate_id : !!order.substrate_id;
    enabledSections.size = isLaminateRequired ? !!order.laminate_id : !!order.substrate_id;
    enabledSections.application =
        enabledSections.size && !!order.width && !!order.length && !!order.depth;
    enabledSections.artwork =
        enabledSections.application && order.form_factor_id != null && order.id > 0;

    const handleMaterialChange = useCallback(
        v => ctx.handleChange({order: {substrate_id: {$set: v}}}),
        [ctx.handleChange]
    );

    const handleLaminateChange = useCallback(
        v => {
            const updateTarget = {order: {laminate_id: {$set: v}}};
            // Automatically choose White Vellum if White Paper and No Laminate is selected
            if (
                v === LAMINATE_NO_LAMINATE &&
                (ctx.order.substrate_id === CARTON_BRIGHT_WHITE_PAPER ||
                    ctx.order.substrate_id === CARTON_WHITE_PAPER ||
                    ctx.order.substrate_id === CARTON_WHITE_PAPER_C2S)
            ) {
                updateTarget.order.substrate_id = {$set: CARTON_UNLAMINATED_WHITE_PAPER};
            } else if (
                v !== LAMINATE_NO_LAMINATE &&
                ctx.order.substrate_id === CARTON_UNLAMINATED_WHITE_PAPER
            ) {
                updateTarget.order.substrate_id = {$set: CARTON_BRIGHT_WHITE_PAPER};
            }
            ctx.handleChange(updateTarget);
        },
        [ctx.handleChange, ctx.order.substrate_id, ctx.order.laminate_id]
    );

    const handleOrderNotes = v => {
        ctx.handleChange({order: {comments_customer: {$set: v}}});
    };

    //Any changes made to the isSnapped parameters should also be made manually in CartonQuoterFeeTrait
    const isCustomFee =
        isNewDie &&
        order.product_type_id === PRODUCT_TYPE_CARTON &&
        ((!isValueMultipleOfStep(width, 8, 3) && isValueMultipleOfStep(width, 2, 1)) ||
            (!isValueMultipleOfStep(length, 8, 3) && isValueMultipleOfStep(length, 2, 1)) ||
            (!isValueMultipleOfStep(depth, 8, 3) && isValueMultipleOfStep(depth, 2, 1)));

    const sortOrder = [
        CARTON_BRIGHT_WHITE_PAPER,
        CARTON_WHITE_PAPER,
        CARTON_METALLIC_PAPER,
        CARTON_BLACK_VELLUM,
        CARTON_HOLOGRAPHIC,
        CARTON_UNLAMINATED_WHITE_PAPER
    ];
    return (
        <div className="row g-lg-7">
            <div className="col-lg-8">
                <Section>
                    <h3 className="mt-3">Select Your Material</h3>
                    <GridSelect
                        minCols={3}
                        maxCols={3}
                        ratio="1x1"
                        options={CARTON_SUBSTRATES.sort((a, b) => {
                            const indexA = sortOrder.indexOf(a.id);
                            const indexB = sortOrder.indexOf(b.id);

                            if (indexA === -1 && indexB === -1) return 0; // Neither a nor b is in sortOrder, keep original order
                            if (indexA === -1) return 1; // Only b is in sortOrder, a goes after b
                            if (indexB === -1) return -1; // Only a is in sortOrder, b goes after a
                            return indexA - indexB; // Both are in sortOrder, sort by their order in sortOrder
                        }).map(s => {
                            const option = {...s};
                            if (
                                option.id === CARTON_BRIGHT_WHITE_PAPER &&
                                order.substrate_id === CARTON_WHITE_PAPER_C2S
                            ) {
                                // If the customer chooses white paper and moisture and oil resistant, select the White Paper Option
                                option.id = CARTON_WHITE_PAPER_C2S;
                                option.value = CARTON_WHITE_PAPER_C2S;
                            }
                            let info = s.info ?? '';
                            if (
                                DUPLEX_SUBSTRATES.indexOf(parseInt(option.id)) === -1 &&
                                option.id !== CARTON_BRIGHT_WHITE_PAPER
                            ) {
                                info = `${info} No Duplex`;
                            }

                            option.description = <OptionDescription option={{...s, info}} />;
                            return option;
                        })}
                        value={order.substrate_id}
                        onChange={handleMaterialChange.bind(this)}
                    />
                </Section>
                <Section visible={enabledSections.laminate}>
                    <h3 className="mt-3">Laminate</h3>
                    <CartonLaminateSelect
                        order={order}
                        onChange={handleLaminateChange.bind(this)}
                    />
                </Section>

                <Section enabled={enabledSections.shape}>
                    <ShapeSelect
                        shapeCategory={shapeCategory}
                        onShapeCategoryChange={setShapeCategory}
                    />
                </Section>
                <ConditionalButton enabled={shapeCategory === SHAPE_CARTON_CUSTOM}>
                    <Section enabled={enabledSections.size}>
                        <h3>Custom Shape</h3>
                        <div>
                            If you have a new custom dieline for your carton,{' '}
                            <button
                                style={{color: '#009940'}}
                                className="btn btn-link p-0 text-primary"
                                onClick={() => {
                                    if (accountCtx.user.is_logged_in) {
                                        Beacon('identify', {
                                            email: accountCtx.user?.email,
                                            name: accountCtx.user?.name,
                                            company: accountCtx.user.company_entity?.name
                                        });
                                    }
                                    Beacon('open');
                                    Beacon('suggest', [
                                        '6525a27c9c4c0a6240b2df32',
                                        '6525a507e18ad86e4ffd946c',
                                        '633d9d49cf38bc37aecf6c54',
                                        '6525b63144252e4a513e96f8',
                                        '5fede5c541fcb56e4047d3cf'
                                    ]);
                                }}>
                                Learn More
                            </button>{' '}
                            about the process.
                            <br /> When you are ready,{' '}
                            <button
                                style={{color: '#009940'}}
                                className="btn btn-link p-0 text-primary"
                                onClick={() => {
                                    if (accountCtx.user.is_logged_in) {
                                        Beacon('identify', {
                                            email: accountCtx.user?.email,
                                            name: accountCtx.user?.name,
                                            company: accountCtx.user.company_entity?.name
                                        });
                                    }
                                    Beacon('open');
                                    Beacon('suggest', [
                                        '6525a27c9c4c0a6240b2df32',
                                        '6525a507e18ad86e4ffd946c',
                                        '633d9d49cf38bc37aecf6c54',
                                        '6525b63144252e4a513e96f8',
                                        '5fede5c541fcb56e4047d3cf'
                                    ]);
                                    Beacon('navigate', '/ask/message/');
                                    Beacon('prefill', {
                                        text: 'I have a custom carton dieline. I have attached the dieline PDF in this email. Please send me the die id when it is ready.'
                                    });
                                }}>
                                Email
                            </button>{' '}
                            or{' '}
                            <button
                                style={{color: '#009940'}}
                                className="btn btn-link p-0 text-primary"
                                onClick={() => {
                                    if (accountCtx.user.is_logged_in) {
                                        Beacon('identify', {
                                            email: accountCtx.user?.email,
                                            name: accountCtx.user?.name,
                                            company: accountCtx.user.company_entity?.name
                                        });
                                    }
                                    Beacon('suggest', [
                                        '6525a27c9c4c0a6240b2df32',
                                        '6525a507e18ad86e4ffd946c',
                                        '633d9d49cf38bc37aecf6c54',
                                        '6525b63144252e4a513e96f8',
                                        '5fede5c541fcb56e4047d3cf'
                                    ]);
                                    Beacon('open');
                                    Beacon('navigate', '/ask/chat/');
                                }}>
                                Chat
                            </button>{' '}
                            to get started.
                        </div>
                        <CustomShapeSelect
                            onChange={v =>
                                ctx.handleUpdate({
                                    order: {
                                        size_id: {$set: v},
                                        shape_id: {$set: SHAPE_CARTON_CUSTOM},
                                        size_type_id: {$set: SIZE_TYPE_STANDARD}
                                    }
                                })
                            }
                            selectedDie={selectedDie}
                            dies={cartonCustomDies}
                        />
                    </Section>
                    <Section enabled={enabledSections.size}>
                        <h3>Select Your Size</h3>
                        <SizeSelect
                            dies={cartonDies}
                            width={width}
                            onWidthChange={setWidth}
                            length={length}
                            onLengthChange={setLength}
                            depth={depth}
                            onDepthChange={setDepth}
                            errors={errors}
                        />
                        <SelectedDie
                            errors={errors}
                            die={selectedDie}
                            isNewDie={isNewDie}
                            productId={order.product_type_id}
                            width={width}
                            length={length}
                            depth={depth}
                            onRoundSize={setAllSizes}
                            quotedDieCharge={ctx.order.quoted_die_charge}
                            isCustomFee={isCustomFee}
                        />
                        <SVGDownload
                            errors={errors}
                            width={order.width}
                            length={order.length}
                            depth={order.depth}
                            shapeId={order.shape_id}
                            isSquareCorners={order.is_square_corners ? 1 : 0}
                        />
                        <SimilarSizesList sizes={cartonSimilarSizes} />
                        <div className="text-center">
                            <ConditionalButton enabled={shapeCategory === SHAPE_CARTON_AUTOLOCK}>
                                <img
                                    src={cartonBoxAutolock}
                                    alt="Carton Dimensions"
                                    className="w-100 mt-4"
                                />
                                <img
                                    src={cartonBoxStraighTuck}
                                    alt="Carton Dimensions"
                                    className="w-100 mt-4"
                                />
                            </ConditionalButton>
                        </div>
                    </Section>
                </ConditionalButton>

                <Section enabled={enabledSections.artwork}>
                    <h3>Upload Artwork</h3>
                    <ArtworkUploader
                        orderId={order.id}
                        isDuplex={order.is_duplex}
                        isFrontBack={order.is_front_back}
                        lastUpdate={ctx.lastUpdate}
                    />

                    <AdditionalOptions errors={errors} />

                    <h6 className="mt-6">Additional Order Notes</h6>
                    <Input
                        rows={4}
                        label="Order Notes"
                        value={order.comments_customer || ''}
                        onChange={handleOrderNotes}
                    />
                </Section>
            </div>

            <div className="col-lg-4">
                <div className="sticky-top pt-5">
                    <h4 className="mt-0">Order Summary</h4>
                    <OrderSummary isCustomFee={isCustomFee} />
                </div>
            </div>
        </div>
    );
}
